// The week calendar per pool: a row per vehicle, a column per day, every booking with who booked
// it, and Boka in a free day (2.3.4, 2.3.5).
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { CalendarView, PoolSummary } from '@aldorock-fordon/shared/api-types';
import { api } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { clock, plate, shortDay, todayIso, weekday } from '../format.ts';

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export function CalendarPage() {
  const h1 = usePage(sv.calendar.title);
  const [params, setParams] = useSearchParams();
  const pool = params.get('pool') ?? '';
  const week = params.get('week') ?? todayIso();
  const pools = useQuery({ queryKey: ['pools'], queryFn: () => api<PoolSummary[]>('/pools') });
  const q = useQuery({
    queryKey: ['calendar', pool, week],
    queryFn: () => api<CalendarView>(`/calendar?week=${week}${pool ? `&pool=${pool}` : ''}`),
  });
  const d = q.data;
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };
  const today = todayIso();
  return (
    <>
      <PageHead help="calendar" title={sv.calendar.title} titleRef={h1} />
      <p>{sv.calendar.intro}</p>
      <div className="filter-row">
        <div className="field">
          <label className="label" htmlFor="pool">
            {sv.calendar.pool}
          </label>
          <div className="select">
            <select
              id="pool"
              value={d?.pool.id ?? pool}
              onChange={(e) => set('pool', e.target.value)}
            >
              {pools.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="actions-inline week-nav">
          <button
            className="btn"
            type="button"
            onClick={() => set('week', addDays(d?.from ?? week, -7))}
          >
            {sv.calendar.previous}
          </button>
          <button className="btn" type="button" onClick={() => set('week', today)}>
            {sv.calendar.today}
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => set('week', addDays(d?.from ?? week, 7))}
          >
            {sv.calendar.next}
          </button>
        </div>
      </div>
      {q.isPending ? <p role="status">{sv.app.loading}</p> : null}
      {d ? (
        <>
          {d.pool.adminNote ? <p className="caption">{d.pool.adminNote}</p> : null}
          <div
            className="table-wrap"
            tabIndex={0}
            role="region"
            aria-label={sv.table.scrollLabel(sv.calendar.title)}
          >
            <table className="table calendar">
              <caption>{sv.calendar.caption(d.pool.name, d.from, d.to)}</caption>
              <thead>
                <tr>
                  <th scope="col">{sv.calendar.vehicleColumn}</th>
                  {d.days.map((day) => (
                    <th scope="col" key={day} className={day === today ? 'is-today' : undefined}>
                      <span className="visually-hidden">{weekday(day)}</span>
                      <span aria-hidden="true">{shortDay(day)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.vehicles.map((v) => (
                  <tr key={v.vehicleId}>
                    <th scope="row" className="row-head">
                      <span className="vehicle-name">
                        {[v.make, v.model].filter(Boolean).join(' ')}
                      </span>
                      <br />
                      <span className="num muted">{plate(v.registrationNumber)}</span>
                      {v.note ? <span className="caption"> {v.note}</span> : null}
                    </th>
                    {v.days.map((cell) => (
                      <td key={cell.date} className={cell.date === today ? 'is-today' : undefined}>
                        <ul className="plain cal-cell">
                          {cell.bookings.map((b) => (
                            <li key={b.id} className={b.own ? 'cal-own' : 'cal-booking'}>
                              <Link to={`/bokning/${b.id}`}>
                                <span className="num">
                                  {sv.range(clock(b.startsAt), clock(b.endsAt))}
                                </span>
                                <br />
                                {b.driver}
                              </Link>
                            </li>
                          ))}
                          {cell.date >= today ? (
                            <li>
                              <Link
                                className="cal-book"
                                to={`/boka?pool=${d.pool.id}&vehicle=${v.vehicleId}&date=${cell.date}`}
                                aria-label={sv.calendar.book(
                                  `${[v.make, v.model].filter(Boolean).join(' ')} ${plate(v.registrationNumber)}`,
                                  weekday(cell.date),
                                )}
                              >
                                {sv.calendar.bookShort}
                              </Link>
                            </li>
                          ) : null}
                        </ul>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  );
}
